
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, Users, MessageSquare, Search, Download } from 'lucide-react';
import type { Contact } from '@/pages/PersonalizeOutreach';

interface ResultsReviewProps {
  contacts: Contact[];
  onContinueToExport: () => void;
}

const ResultsReview = ({ contacts, onContinueToExport }: ResultsReviewProps) => {
  const [expandedContacts, setExpandedContacts] = useState<Set<string>>(new Set());

  const toggleExpanded = (contactId: string) => {
    setExpandedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedContacts(new Set(contacts.map(c => c.id)));
  };

  const collapseAll = () => {
    setExpandedContacts(new Set());
  };

  const completedContacts = contacts.filter(c => c.research && c.personalizedMessage);
  const failedContacts = contacts.length - completedContacts.length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Review Research Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Successful</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{completedContacts.length}</p>
              <p className="text-sm text-green-700">With research & messages</p>
            </div>
            
            {failedContacts > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-900">Failed</span>
                </div>
                <p className="text-2xl font-bold text-red-900">{failedContacts}</p>
                <p className="text-sm text-red-700">Need manual review</p>
              </div>
            )}
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-900">Total Contacts</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{contacts.length}</p>
              <p className="text-sm text-blue-700">Ready for outreach</p>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <p className="text-gray-600">
              Review the AI-generated research and personalized messages below. You can expand individual contacts or view all at once.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>
                <Eye className="h-4 w-4 mr-2" />
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                <EyeOff className="h-4 w-4 mr-2" />
                Collapse All
              </Button>
            </div>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium">{contact.person}</h4>
                    <p className="text-sm text-gray-600">{contact.organization}</p>
                    {contact.title && (
                      <p className="text-xs text-gray-500">{contact.title}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={contact.research && contact.personalizedMessage ? 'default' : 'destructive'}
                    >
                      {contact.research && contact.personalizedMessage ? 'Complete' : 'Failed'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(contact.id)}
                    >
                      {expandedContacts.has(contact.id) ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {expandedContacts.has(contact.id) && (
                  <div className="space-y-4 pt-3 border-t">
                    {contact.research && (
                      <div>
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          AI Research:
                        </h5>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded leading-relaxed">
                          {contact.research}
                        </p>
                      </div>
                    )}
                    
                    {contact.personalizedMessage && (
                      <div>
                        <h5 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Personalized Message:
                        </h5>
                        <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded leading-relaxed">
                          {contact.personalizedMessage}
                        </p>
                      </div>
                    )}

                    {(!contact.research || !contact.personalizedMessage) && (
                      <div className="bg-red-50 p-3 rounded">
                        <p className="text-sm text-red-700">
                          Research or message generation failed for this contact. You may want to manually research this prospect.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-6 border-t">
            <Button onClick={onContinueToExport} size="lg" className="px-8">
              <Download className="h-4 w-4 mr-2" />
              Continue to Export
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultsReview;
